import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Route, Routes, useNavigate, useLocation, useResolvedPath } from 'react-router-dom';
import { useFullReset, useKioskState } from '../../context/Kiosk/kiosk';
import { IAppointment } from '../../API/GoMoto';
import { Page } from '../Layout/Page/Page';
import { LoadAppointment } from '../Utility/LoadAppointment';
import { AppointmentDispatchTypes, AppointmentProvider, useAppointmentDispatch, useAppointmentStateUnsafe } from '../../context/Appointment/appointment';
import { LoadingRoute } from '../../controls/Route/LoadingRoute';
import { AppRoutePaths, OnlinePaymentRoutePaths, OutdoorVehiclePickUpRoutePaths, PathName, PickUpQRCodeRoutePaths } from '../../helpers/Routes';
import { StripePaymentStatus } from './PaymentStatus/StripePaymentStatus';
import { WorldPayPaymentStatus } from './PaymentStatus/WorldPayPaymentStatus';
import { StripePayment } from './Payment/StripePayment';
import { WorldPayPayment } from './Payment/WorldPayPayment';
import { PaymentLanding } from './PaymentLanding/PaymentLanding';
import { PaymentProvider } from '../../API/Enums';
import { Spinner } from '../../controls/Spinner/Spinner';
import { useLocalization } from '../../context/Localization/localization';

export const OnlinePaymentIndex: React.FC = React.memo(() => {
    const Localization = useLocalization();
    const embedded = useMemo<boolean>(() => !!window.top && !!new URLSearchParams(window.location.search)
        .get('TransactionSetupID') && window.parent !== window, []);

    useLayoutEffect(() => {
        /*
         * HACK Since we use an iframe to embed WorldPay, the "redirect" back to use embeds our site within our site
         * Reload the top level site with the embedded version
         */
        if (embedded) {
            window.top?.location.replace(window.location.pathname + window.location.search);
        }
    }, [embedded]);

    return (
        <AppointmentProvider
            useCache
            suppressBgCalls
            suppressRecallCalls
        >
            <Page
                suppressLocalizationButtons
                showKioskBanner={false}
                customHeaderProps={embedded ? null : {
                    title: Localization.PayForService,
                }}
            >
                {embedded ? <Spinner /> : <OnlinePaymentRoutes />}
            </Page>
        </AppointmentProvider>
    );
});

const OnlinePaymentRoutes: React.FC = React.memo(() => {
    const { pathname: basePath } = useResolvedPath('.');
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const reset = useFullReset(true, false);
    const {
        kioskType,
        dealer: { key: dealerKey, paymentProvider },
    } = useKioskState();
    const appointment = useAppointmentStateUnsafe();
    const appointmentDispatch = useAppointmentDispatch();
    const [worldPayPaymentErrorMessage, setWorldPayPaymentErrorMessage] = useState<string>('');

    const reloadAppointment = useCallback(() => {
        navigate(PathName(basePath, OnlinePaymentRoutePaths.LoadAppointment, appointment?.key ?? ''), { replace: true });
    }, [navigate, basePath, appointment]);

    useEffect(() => {
        /*
         * safeguards for when refreshing the site on a subpage. Appointment is stored in the cache, but the kiosk
         * will need to be reinitialed
         */
        if (
            !pathname.includes(PathName(AppRoutePaths.OnlinePayment, OnlinePaymentRoutePaths.LoadAppointment)) &&
            appointment &&
            appointment.dealerKey !== dealerKey
        ) {
            reloadAppointment();
        }
    }, [pathname, reloadAppointment, appointment, dealerKey]);

    const onPaymentStatusGetKeys = useCallback(() => {
        if (kioskType === 'outdoor') {
            navigate(PathName(AppRoutePaths.OutdoorVehiclePickUp, OutdoorVehiclePickUpRoutePaths.Appointment, appointment?.key ?? ''));
        }
        else {
            navigate(PathName(AppRoutePaths.PickupQRCode, PickUpQRCodeRoutePaths.Appointment, appointment?.key ?? ''));
        }
    }, [navigate, appointment, kioskType]);

    const onStripePaymentStatusReturn = useCallback(() => {
        navigate(PathName(basePath, OnlinePaymentRoutePaths.Landing), { replace: true });
    }, [navigate, basePath]);

    const onWorldPayPaymentStatusReturn = useCallback((route: OnlinePaymentRoutePaths, errorMessage: string = '') => {
        setWorldPayPaymentErrorMessage(errorMessage);

        navigate(PathName(basePath, route), { replace: true });
    }, [navigate, basePath]);

    const onPaymentSkip = useCallback((sessionId?: string) => {
        navigate(PathName(basePath, `${OnlinePaymentRoutePaths.PaymentStatus}${sessionId ? `?session_id=${sessionId}` : ''}`), {
            replace: true,
        });
    }, [navigate, basePath]);

    const onWorldPayTransactionError = useCallback((error: string) => {
        setWorldPayPaymentErrorMessage(error);

        navigate(PathName(basePath, `${OnlinePaymentRoutePaths.Landing}`), { replace: true });
    }, [basePath, navigate]);

    const onPaymentLandingSkip = useCallback(() => {
        navigate(PathName(basePath, OnlinePaymentRoutePaths.PaymentStatus), {
            replace: true,
        });
    }, [navigate, basePath]);

    const onPaymentLandingProceed = useCallback(() => {
        navigate(PathName(basePath, OnlinePaymentRoutePaths.Payment), {
            replace: true,
        });
    }, [navigate, basePath]);

    return (
        <Routes>
            <Route
                path={PathName(OnlinePaymentRoutePaths.LoadAppointment, ':apptKey', '*')}
                element={
                    <LoadAppointment
                        onLoaded={(loadedApt: IAppointment) => {
                            if (!loadedApt) {
                                reset();
                            }
                            else {
                                appointmentDispatch({ type: AppointmentDispatchTypes.Update,
                                    payload: loadedApt });
                                if (loadedApt.paymentStatus === 'paid') {
                                    if (paymentProvider === PaymentProvider.StripeCanada) {
                                        /*
                                         * The Stripe session creation will grab the completed session and redirect to the payment status screen
                                         * when the appointment has already been paid
                                         */
                                        navigate(PathName(basePath, OnlinePaymentRoutePaths.Payment), { replace: true });
                                    }
                                    else {
                                        navigate(PathName(basePath, OnlinePaymentRoutePaths.PaymentStatus), { replace: true });
                                    }
                                }
                                else if (paymentProvider === PaymentProvider.StripeCanada) {
                                    const redirectUrl: string | null = new URLSearchParams(window.location.search)
                                        .get('redirect_url');

                                    if (redirectUrl) {
                                        navigate(redirectUrl, { replace: true });
                                    }
                                    else {
                                        navigate(PathName(basePath, OnlinePaymentRoutePaths.Landing), { replace: true });
                                    }
                                }
                                else if (paymentProvider === PaymentProvider.WorldPayUS) {
                                    const transactionSetupId: string | null = new URLSearchParams(window.location.search)
                                        .get('TransactionSetupID');
                                    const hostedPaymentStatus: string | null = new URLSearchParams(window.location.search)
                                        .get('HostedPaymentStatus');

                                    // If we have a transaction setup ID, that means we are returning from the payment form.
                                    if (transactionSetupId && hostedPaymentStatus !== 'Cancelled') {
                                        navigate(PathName(basePath, OnlinePaymentRoutePaths.PaymentStatus) + window.location.search, { replace: true });
                                    }
                                    else if (hostedPaymentStatus === 'Cancelled') {
                                        navigate(PathName(basePath, OnlinePaymentRoutePaths.Payment), { replace: true });
                                    }
                                    else {
                                        navigate(PathName(basePath, OnlinePaymentRoutePaths.Landing), { replace: true });
                                    }
                                }
                            }
                        }}
                    />
                }
            />
            <Route
                path={PathName(OnlinePaymentRoutePaths.Landing, '*')}
                element={
                    <LoadingRoute loaded={!!appointment && appointment.dealerKey === dealerKey}>
                        <PaymentLanding
                            onSkip={onPaymentLandingSkip}
                            onProceed={onPaymentLandingProceed}
                            errorMessage={worldPayPaymentErrorMessage}
                        />
                    </LoadingRoute>
                }
            />
            <Route
                path={PathName(OnlinePaymentRoutePaths.Payment, '*')}
                element={
                    <LoadingRoute loaded={!!appointment && appointment.dealerKey === dealerKey}>
                        {paymentProvider === PaymentProvider.StripeCanada ?
                            <StripePayment
                                onSkip={onPaymentSkip}
                                onReload={reloadAppointment}
                            /> :
                            <WorldPayPayment
                                onSkip={onPaymentSkip}
                                onReload={reloadAppointment}
                                onError={onWorldPayTransactionError}
                                initialErrorMessage={worldPayPaymentErrorMessage}
                            />
                        }
                    </LoadingRoute>
                }
            />
            <Route
                path={PathName(OnlinePaymentRoutePaths.PaymentStatus, '*')}
                element={
                    <LoadingRoute loaded={!!appointment && appointment.dealerKey === dealerKey}>
                        {paymentProvider === PaymentProvider.StripeCanada ?
                            <StripePaymentStatus
                                onGetKeys={onPaymentStatusGetKeys}
                                onReturn={onStripePaymentStatusReturn}
                            /> :
                            <WorldPayPaymentStatus
                                onGetKeys={onPaymentStatusGetKeys}
                                onReturn={onWorldPayPaymentStatusReturn}
                            />
                        }
                    </LoadingRoute>
                }
            />
        </Routes>
    );
});
